import { find, isDate, isNumber, isString } from 'lodash';
import moment from 'moment-timezone';

import DATE_FORMATS from '../../../utilities/DateTimeFormats';
import { _1_DAY_TIME_RANGE, getComparisonPeriod, title } from './TimeRangeComparisonFilterConstants';
import { dateTimeFormatter } from '../../../utilities/FormattingUtilities';
import { getTimeZone } from '../../../stores/SessionStore';
import { getTimeSyncMessage } from '../time-range-filter/TimeRangeFilterStateService';
import { getTimeZoneOptions } from '../../form/FormConstants';

/**
 * Gets a time zone object with both full name and an abbreviation
 * Accepts a parameter with the time zone name, otherwise default to user's time zone
 */
export function getTimeZoneObject(timeZoneNameParam) {
    const timeZoneName = timeZoneNameParam || getTimeZone();
    const timeZoneFull = find(getTimeZoneOptions(), (tzValue) => {
        return tzValue.name === timeZoneName || tzValue.pretty === timeZoneName;
    });
    // Save the abbreviation (the bit in parantheses) for the short summary string:
    // example: America/Denver (MDT)
    // const timeZoneShort = get(timeZoneFull?.pretty?.split('(').pop().split(')'), 0);
    const timeZoneShort = moment.tz(timeZoneName).format('z');

    return { ...timeZoneFull, short: timeZoneShort };
}

/**
 * @param string period
 * @param Date|null startParam
 * @param Date|null endParam
 */
export function getComparisonPeriodTimeRange(period, startParam, endParam) {
    let start;
    let end;
    if (startParam) {
        start = startParam;
        end = moment(start).add(period.durationInMinutes, 'minutes').toDate();
    } else if (endParam) {
        end = endParam;
        // Subtract the last-n duration from the end time to arrive at the start time.
        start = moment(end).subtract(period.durationInMinutes, 'minutes').toDate();
    }

    // Return the aligned UTC range for the query.
    return {
        id: period.id,
        start,
        end,
    };
}

function encodeRange(range) {
    const { start, end } = range;

    let startMs;
    let endMs;

    if (isDate(start) || isString(start)) {
        startMs = new Date(start).getTime();
    } else if (isNumber(start)) {
        startMs = start;
    }

    if (isDate(end) || isString(end)) {
        endMs = new Date(end).getTime();
    } else if (isNumber(end)) {
        endMs = end;
    }

    return `${startMs},${endMs}`;
}

/**
 * Get the start of the current day (midnight)
 *
 * @returns {Date}
 */
function getStartOfYesterday(timeZoneName) {
    const now = moment.tz(timeZoneName);
    const startOfToday = now.startOf('day');
    return startOfToday.subtract(24, 'hours').toDate();
}

const TimeRangeComparisonFilterStateServiceFactory = (filterId) => {
    const TimeRangeComparisonFilterStateService = {
        /*
         * Factory functions
         */

        createWithEncodedState(encodedState) {
            const state = TimeRangeComparisonFilterStateService.decode(encodedState);

            return state;
        },

        createWithDefaultState() {
            const tzFull = getTimeZoneObject();
            const period = _1_DAY_TIME_RANGE;
            const yesterday = getStartOfYesterday(tzFull.name);
            const before = getComparisonPeriodTimeRange(period, null, yesterday);
            const after = getComparisonPeriodTimeRange(period, yesterday, null);

            return {
                before: {
                    ...before,
                    id: period.id,
                },
                after: {
                    ...after,
                    id: period.id,
                },
                timeZone: tzFull,
            };
        },

        createState(encodedFilterState, dataService) {
            const encodedState = encodedFilterState?.[filterId];
            return encodedState
                ? this.createWithEncodedState(encodedState, dataService)
                : this.createWithDefaultState(dataService);
        },

        /**
         * Auto-update functions.
         */

        shouldRefreshState(currentState) {
            return getComparisonPeriod(currentState?.id) !== undefined;
        },

        refreshState(currentState) {
            // Nothing to refresh for custom time ranges, so just return the current state as a new
            // object so it triggers a state change.
            return { ...currentState };
        },

        /*
         * Transformation functions
         */

        parameterize(state) {
            return {
                afterEnd: `${state.after.end.getTime()}`,
                afterStart: `${state.after.start.getTime()}`,
                beforeEnd: `${state.before.end.getTime()}`,
                beforeStart: `${state.before.start.getTime()}`,
            };
        },

        encode(state) {
            const { before, after } = state;
            return `${state.before.id},${encodeRange(before)},${state.after.id},${encodeRange(after)},${
                state.timeZone.name // URL encoding only gets the "name" string, without abbreviation
            }`;
        },

        decode(encodedState) {
            const values = encodedState.split(',');
            if (values.length !== 7) return undefined;
            const [beforeId, beforeStart, beforeEnd, afterId, afterStart, afterEnd, timeZoneName] = values;
            const timeZone = getTimeZoneObject(timeZoneName);

            return {
                before: {
                    id: beforeId,
                    start: new Date(parseInt(beforeStart, 10)),
                    end: new Date(parseInt(beforeEnd, 10)),
                },
                after: {
                    id: afterId,
                    start: new Date(parseInt(afterStart, 10)),
                    end: new Date(parseInt(afterEnd, 10)),
                },
                timeZone,
            };
        },

        summarize(state) {
            const beforePeriod = getComparisonPeriod(state.before.id);
            const afterPeriod = getComparisonPeriod(state.after.id);
            const timeZone = state.timeZone.name;
            let summaryText;

            // Special case where the anchor period is a single date and both comparison periods are the same.
            if (state.before.id === state.after.id && state.before.end.getTime() === state.after.start.getTime()) {
                const formattedBeforeDateTime = dateTimeFormatter(state.before.end, {
                    toFormat: DATE_FORMATS.DATETIME_MIN_12H_TZ,
                    timeZone,
                });
                summaryText = `${beforePeriod.name} Before/After ${formattedBeforeDateTime}`;
            } else {
                // skip TZ on before text, since it'll be in the after text, and use the abbreviation
                const dateTimeBefore = dateTimeFormatter(state.before.end, {
                    toFormat: DATE_FORMATS.DATETIME_MIN_12H,
                    timeZone,
                });
                const dateTimeAfter = dateTimeFormatter(state.after.start, {
                    toFormat: DATE_FORMATS.DATETIME_MIN_12H_TZ,
                    timeZone,
                });
                summaryText = `${beforePeriod.name} Before ${dateTimeBefore} & ${afterPeriod.name} After ${dateTimeAfter}`;
            }

            return {
                title,
                value: summaryText,
                shortValue: summaryText,
                infoMessage: getTimeSyncMessage(),
            };
        },
    };

    return TimeRangeComparisonFilterStateService;
};

export default TimeRangeComparisonFilterStateServiceFactory;
